import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { QueryClient, VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query';

import App from './App.vue';
import { router } from './router';
import { i18n } from './i18n';
import { configureAmplify } from './composables/useAmplifyBootstrap';
import { configureTokenStorageFromPreference } from './auth/rememberMe';
import { installAuthGuard } from './router/guards';
import { installResponseAuthHandler } from './api/httpClient';
import { useAuthStore } from './stores/auth';
import { useOrgStore } from './stores/org';
import { deriveTenantSlugFromHost } from './lib/tenantHost';

import { IS_PRODUCTION } from './config/featureFlags';

import './style.css';
import './app-elements.css';
import './config/prod-hidden.css';

async function bootstrap() {
  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);

  // Read by src/config/prod-hidden.css to hide production-only nav entries
  // (src/config/featureFlags.ts). Stamped before mount so first paint is
  // already correct — no flash of hidden content.
  document.documentElement.dataset.env = IS_PRODUCTION ? 'production' : 'development';

  const orgStore = useOrgStore(pinia);
  const authStore = useAuthStore(pinia);

  // Phase 1: hydrate the org's sign-in config (or fall back to env Cognito).
  //
  // Resolution order:
  //   1. sessionStorage-pinned orgId (within-session continuity).
  //   2. Host-derived slug if the leftmost label of the hostname is a
  //      tenant slug (e.g. `dcc.optimal-hr.com` → "dcc"). Lets a user
  //      who bookmarked their tenant URL skip the workspace picker.
  //   3. Otherwise no org context — boot lands on /welcome.
  //
  // A backend failure on path 2 falls back to /welcome cleanly so a
  // typo / decommissioned tenant doesn't brick the bundle.
  const sessionOrgId = orgStore.orgId;
  const hostSlug = sessionOrgId ? null : deriveTenantSlugFromHost(window.location.hostname);
  const derivedOrgId = sessionOrgId ?? hostSlug;
  if (derivedOrgId) {
    try {
      await orgStore.selectOrg(derivedOrgId);
      configureAmplify(orgStore.signInConfig);
    } catch {
      configureAmplify(null);
    }
  } else {
    configureAmplify(null);
  }

  // Point Amplify's Cognito token provider at the storage adapter the
  // user last chose via "Remember me" (localStorage = persistent,
  // sessionStorage = cleared on close). Must run BEFORE the session is
  // rehydrated below so tokens are read from the right store.
  configureTokenStorageFromPreference();

  // Phase 2: try to restore an existing Cognito session before the first nav.
  await authStore.refreshFromSession();

  // Phase 3: wire HTTP 401 → signOut and the protected-route guard.
  installResponseAuthHandler({
    onUnauthorized: async () => {
      await authStore.signOut();
      router.push({ name: 'sign-in' });
    },
  });
  installAuthGuard(router);

  app.use(router);
  app.use(i18n);
  // Explicit TanStack Query defaults (Wave 3.2 perf hardening). Per-query
  // staleTime overrides set in each composable still apply; this just
  // makes the cross-cutting defaults visible at the boot site:
  //
  //   - gcTime 5 min: unmounted query caches stick around long enough
  //     that a quick tab-switch / drawer-toggle hits cache, not network.
  //   - refetchOnWindowFocus false: dev / prod observability is already
  //     handled by per-screen Confirm-and-Continue invalidations; a
  //     tab-switch refetch storm only adds noise.
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    },
  });
  const vueQueryPluginOptions: VueQueryPluginOptions = { queryClient };
  app.use(VueQueryPlugin, vueQueryPluginOptions);

  await router.isReady();
  app.mount('#app');
}

void bootstrap();
